import api from '@services/api';
import {
  CONTEST_AGE_CATEGORIES,
  CONTEST_SECTION_ORDER,
  CONTEST_TYPE_OPTIONS,
  PLATFORM_GRADE_ORDER,
  getSportContestCatalog,
  isRequiredFrame2Section,
  normalizeCapabilityLevel,
  visibleContestGradeCodes,
} from './contestCatalog';
import type {
  ContestBenchmark,
  ContestCapabilityLevel,
  ContestCatalogEntry,
  ContestCategoryId,
  ContestCategoryGroup,
  ContestGenderKey,
  ContestGradeBand,
  ContestMediaItem,
  ContestSection,
  ContestTypeId,
  SportContestCatalog,
} from '../types/contestCatalog';

interface ApiEnabledGrade {
  gradeCode: string;
  isEnabled?: boolean;
}

interface ApiBenchmarkRow {
  ageCategory: string;
  gradeCode: string;
  genderKey?: string | null;
  metricType?: string;
  unitLabel?: string | null;
  minValue?: number | null;
  maxValue?: number | null;
}

interface ApiAgeLimitRow {
  ageCategory: string;
  genderKey?: string | null;
  recommendedMaxTime?: number | null;
  timeUnitLabel?: string;
  metricUnitLabel?: string | null;
}

export interface ApiCatalogSkill {
  id: string;
  slug: string;
  title: string;
  subtitle?: string;
  description?: string;
  category: string;
  contestType?: string;
  difficultyLevel?: number | null;
  capabilityLevel?: ContestCapabilityLevel | string | null;
  commonMistakes?: string[] | null;
  sections?: ContestSection[] | null;
  media?: ContestMediaItem[] | null;
  enabledGrades?: ApiEnabledGrade[];
  benchmarks?: ApiBenchmarkRow[];
  ageLimits?: ApiAgeLimitRow[];
}

interface ApiCategoryRow {
  id: string;
  label: string;
  sortOrder?: number;
}

function formatGradeLabel(min?: number | null, max?: number | null): string {
  if (min != null && max != null) return `${min}–${max}`;
  if (min != null && max == null) return `${min}+`;
  if (min == null && max != null) return `≤${max}`;
  return '—';
}

function sortGradeCodes(codes: string[]): string[] {
  const rank = new Map(PLATFORM_GRADE_ORDER.map((c, i) => [String(c), i]));
  return [...codes].sort((a, b) => {
    const ra = rank.has(a) ? (rank.get(a) as number) : 1000;
    const rb = rank.has(b) ? (rank.get(b) as number) : 1000;
    return ra - rb || a.localeCompare(b);
  });
}

function resolveGradeOrder(skill: ApiCatalogSkill, platformGrades: string[]): string[] {
  const enabled = (skill.enabledGrades || [])
    .filter((g) => g.isEnabled !== false)
    .map((g) => String(g.gradeCode || '').trim())
    .filter(Boolean);
  const platform = platformGrades.length ? platformGrades : [...PLATFORM_GRADE_ORDER];
  // Never drop D / A+ just because a skill (or seed) only published C/B/A.
  return visibleContestGradeCodes(
    sortGradeCodes([...new Set([...visibleContestGradeCodes(), ...platform, ...enabled])]),
  );
}

function bandFromRow(row?: ApiBenchmarkRow): ContestGradeBand {
  return {
    code: row?.gradeCode || '',
    label: formatGradeLabel(row?.minValue, row?.maxValue),
    min: row?.minValue ?? undefined,
    max: row?.maxValue ?? undefined,
  };
}

function rowHasValues(row?: ApiBenchmarkRow): boolean {
  return row != null && (row.minValue != null || row.maxValue != null);
}

/** Prefer primary gender rows; fill missing grades from fallback rows (other gender / legacy all). */
function buildGradeBandsFromRows(
  gradeOrder: string[],
  primaryRows: ApiBenchmarkRow[],
  fallbackRows: ApiBenchmarkRow[] = [],
): ContestGradeBand[] {
  return gradeOrder.map((code) => {
    const primary = primaryRows.find((r) => r.gradeCode === code);
    if (rowHasValues(primary)) {
      return { ...bandFromRow(primary), code };
    }
    const fallback = fallbackRows.find((r) => r.gradeCode === code);
    if (rowHasValues(fallback)) {
      return { ...bandFromRow(fallback), code };
    }
    return {
      code,
      label: '—',
      min: undefined,
      max: undefined,
    };
  });
}

function normalizeApiGenderKey(raw: string | null | undefined): 'all' | ContestGenderKey {
  const g = String(raw || 'all').trim().toLowerCase();
  if (g === 'boys' || g === 'boy' || g === 'male' || g === 'm') return 'boys';
  if (g === 'girls' || g === 'girl' || g === 'female' || g === 'f') return 'girls';
  if (g === 'open') return 'open';
  if (g === 'diverse' || g === 'gender_diverse' || g === 'other') return 'diverse';
  return 'all';
}

function buildBenchmarksFromApi(skill: ApiCatalogSkill, platformGrades: string[] = []): Record<string, ContestBenchmark> {
  const gradeOrder = resolveGradeOrder(skill, platformGrades);

  const out: Record<string, ContestBenchmark> = {};

  for (const age of CONTEST_AGE_CATEGORIES) {
    const ageLimitsForAge = (skill.ageLimits || []).filter((r) => r.ageCategory === age);
    const sharedLimit = ageLimitsForAge.find((r) => normalizeApiGenderKey(r.genderKey) === 'all');
    const boysLimit = ageLimitsForAge.find((r) => normalizeApiGenderKey(r.genderKey) === 'boys');
    const girlsLimit = ageLimitsForAge.find((r) => normalizeApiGenderKey(r.genderKey) === 'girls');
    const openLimit = ageLimitsForAge.find((r) => normalizeApiGenderKey(r.genderKey) === 'open');
    const diverseLimit = ageLimitsForAge.find((r) => normalizeApiGenderKey(r.genderKey) === 'diverse');
    const ageLimit = boysLimit || girlsLimit || sharedLimit;

    const rowsForAge = (skill.benchmarks || []).filter((b) => b.ageCategory === age);
    if (!rowsForAge.length && !ageLimitsForAge.some((r) => r.recommendedMaxTime != null)) continue;

    const unit =
      ageLimit?.metricUnitLabel?.trim() ||
      sharedLimit?.metricUnitLabel?.trim() ||
      rowsForAge.find((r) => r.unitLabel)?.unitLabel ||
      '';

    const allRows = rowsForAge.filter((r) => normalizeApiGenderKey(r.genderKey) === 'all');
    const boysRows = rowsForAge.filter((r) => normalizeApiGenderKey(r.genderKey) === 'boys');
    const girlsRows = rowsForAge.filter((r) => normalizeApiGenderKey(r.genderKey) === 'girls');
    const openRows = rowsForAge.filter((r) => normalizeApiGenderKey(r.genderKey) === 'open');
    const diverseRows = rowsForAge.filter((r) => normalizeApiGenderKey(r.genderKey) === 'diverse');

    // Merge incomplete Boy/Girl matrices with each other and legacy "all" so users
    // still see values when only one gender (or shared) row was saved for a grade.
    const boysGrades = buildGradeBandsFromRows(
      gradeOrder,
      boysRows.length ? boysRows : allRows,
      [...allRows, ...girlsRows],
    );
    const girlsGrades = buildGradeBandsFromRows(
      gradeOrder,
      girlsRows.length ? girlsRows : allRows,
      [...allRows, ...boysRows],
    );
    const sharedGrades = allRows.length
      ? buildGradeBandsFromRows(gradeOrder, allRows, [...boysRows, ...girlsRows])
      : boysGrades;

    // Open / Gender diverse fall back to the shared row only — never to Male/Female values.
    const openGrades = buildGradeBandsFromRows(gradeOrder, openRows.length ? openRows : allRows, allRows);
    const diverseGrades = buildGradeBandsFromRows(
      gradeOrder,
      diverseRows.length ? diverseRows : allRows,
      allRows,
    );

    // Divisions with no row of their own keep the authored unit (usually Male/Female)
    // so an unset Open target never flips "sec" into "min".
    const fallbackTimeUnit =
      sharedLimit?.timeUnitLabel || boysLimit?.timeUnitLabel || girlsLimit?.timeUnitLabel || 'min';

    const resolveTime = (limit?: ApiAgeLimitRow) => ({
      recommendedMaxTime: limit?.recommendedMaxTime ?? sharedLimit?.recommendedMaxTime ?? undefined,
      timeUnit: limit?.timeUnitLabel || fallbackTimeUnit,
      unit: limit?.metricUnitLabel?.trim() || unit || undefined,
    });

    const boysTime = resolveTime(boysLimit);
    const girlsTime = resolveTime(girlsLimit);
    const openTime = resolveTime(openLimit);
    const diverseTime = resolveTime(diverseLimit);

    out[age] = {
      unit: unit || undefined,
      metricType: (rowsForAge[0]?.metricType as ContestBenchmark['metricType']) || 'count',
      recommendedMaxTime: sharedLimit?.recommendedMaxTime ?? boysTime.recommendedMaxTime ?? girlsTime.recommendedMaxTime,
      timeUnit: sharedLimit?.timeUnitLabel || boysTime.timeUnit || 'min',
      grades: sharedGrades || boysGrades,
      byGender: {
        boys: {
          grades: boysGrades,
          recommendedMaxTime: boysTime.recommendedMaxTime,
          timeUnit: boysTime.timeUnit,
          unit: boysTime.unit,
        },
        girls: {
          grades: girlsGrades,
          recommendedMaxTime: girlsTime.recommendedMaxTime,
          timeUnit: girlsTime.timeUnit,
          unit: girlsTime.unit,
        },
        open: {
          grades: openGrades,
          recommendedMaxTime: openTime.recommendedMaxTime,
          timeUnit: openTime.timeUnit,
          unit: openTime.unit,
        },
        diverse: {
          grades: diverseGrades,
          recommendedMaxTime: diverseTime.recommendedMaxTime,
          timeUnit: diverseTime.timeUnit,
          unit: diverseTime.unit,
        },
      },
    };
  }

  return out;
}

function defaultSections(skill: ApiCatalogSkill) {
  const blurb = skill.description?.trim() || `Performance standards for ${skill.title}.`;
  return CONTEST_SECTION_ORDER.map(({ key, label }) => ({
    key,
    label,
    hidden: false,
    content: key === 'scoring' ? blurb : 'Detailed content will be published from admin.',
  }));
}

/** Ensure Frame 2 always has every platform section key (seed may omit newer ones). */
function mergeSectionsWithPlatformOrder(
  sections: ContestCatalogEntry['sections'] | undefined,
  skill: ApiCatalogSkill,
): ContestCatalogEntry['sections'] {
  const base = sections?.length ? sections : defaultSections(skill);
  const byKey = new Map(base.map((s) => [s.key, s]));
  return CONTEST_SECTION_ORDER.map(({ key, label }) => {
    const existing = byKey.get(key);
    if (existing) {
      return {
        ...existing,
        label: existing.label || label,
        hidden: isRequiredFrame2Section(key) ? false : Boolean(existing.hidden),
      };
    }
    return {
      key,
      label,
      hidden: false,
      content: key === 'scoring'
        ? (skill.description?.trim() || `Performance standards for ${skill.title}.`)
        : 'Detailed content will be published from admin.',
    };
  });
}

function normalizeCommonMistakes(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  value.forEach((item) => {
    const text = String(item || '').trim();
    if (!text) return;
    const key = text.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(text);
  });
  return out;
}

function mapApiSkillToEntry(
  skill: ApiCatalogSkill,
  seedEntry?: ContestCatalogEntry,
  platformGrades: string[] = [],
): ContestCatalogEntry {
  const authoredSections = Array.isArray(skill.sections) ? skill.sections : null;
  const authoredMedia = Array.isArray(skill.media) ? skill.media : null;
  const entry: ContestCatalogEntry = {
    id: skill.slug,
    title: skill.title,
    subtitle: skill.subtitle,
    description: skill.description,
    difficultyLevel: skill.difficultyLevel ?? undefined,
    capabilityLevel: normalizeCapabilityLevel(skill.capabilityLevel),
    commonMistakes: normalizeCommonMistakes(skill.commonMistakes),
    benchmarks: buildBenchmarksFromApi(skill, platformGrades),
    sections: mergeSectionsWithPlatformOrder(authoredSections ?? seedEntry?.sections, skill),
    media: authoredMedia ?? (seedEntry?.media?.length ? seedEntry.media : []),
  };
  return entry;
}

function findSeedEntry(
  seed: SportContestCatalog | null,
  contestTypeId: ContestTypeId,
  slug: string,
): ContestCatalogEntry | undefined {
  if (!seed) return undefined;
  const typeGroup = seed.contestTypes.find((t) => t.id === contestTypeId);
  for (const cat of typeGroup?.categories ?? []) {
    const found = cat.contests.find((c) => c.id === slug);
    if (found) return found;
  }
  return undefined;
}

function buildCategoryGroupsFromDb(
  skills: ApiCatalogSkill[],
  categories: ApiCategoryRow[],
  seed: SportContestCatalog | null,
  contestTypeId: ContestTypeId,
  platformGrades: string[] = [],
): ContestCategoryGroup[] {
  const seedType = seed?.contestTypes.find((t) => t.id === contestTypeId);
  const seedCategories = seedType?.categories ?? [];

  const categoryMeta = new Map<string, { id: ContestCategoryId; label: string; sortOrder: number }>();

  categories.forEach((c, i) => {
    categoryMeta.set(c.id, {
      id: c.id as ContestCategoryId,
      label: c.label,
      sortOrder: c.sortOrder ?? i,
    });
  });

  seedCategories.forEach((c, i) => {
    if (!categoryMeta.has(c.id)) {
      categoryMeta.set(c.id, { id: c.id, label: c.label, sortOrder: 100 + i });
    }
  });

  skills.forEach((skill) => {
    if (!categoryMeta.has(skill.category)) {
      categoryMeta.set(skill.category, {
        id: skill.category as ContestCategoryId,
        label: skill.category,
        sortOrder: 200,
      });
    }
  });

  const orderedCategories = [...categoryMeta.values()].sort((a, b) => a.sortOrder - b.sortOrder);
  const seenSkillIds = new Set<string>();

  return orderedCategories.map((cat) => {
    const dbEntries = skills
      .filter((s) => s.category === cat.id)
      .map((s) => mapApiSkillToEntry(s, findSeedEntry(seed, contestTypeId, s.slug), platformGrades))
      .filter((entry) => {
        const id = String(entry?.id || '');
        if (!id || seenSkillIds.has(id)) return false;
        seenSkillIds.add(id);
        return true;
      });

    return {
      id: cat.id,
      label: cat.label,
      // DB is source of truth once admin has published skills.
      // Seed still fills Frame 2/3 until those fields are saved on the skill.
      contests: dbEntries,
    };
  }).filter((g) => g.contests.length > 0);
}

export async function loadMergedSportContestCatalog(
  sportName: string,
  contestTypeId: ContestTypeId = 'foundation',
): Promise<{ catalog: SportContestCatalog | null; fromDb: boolean }> {
  const seed = getSportContestCatalog(sportName);

  try {
    const cacheBust = Date.now();
    const [skillsRes, catsRes, gradesRes] = await Promise.all([
      api.get(`/contest-catalog/${encodeURIComponent(sportName)}`, {
        params: { _t: cacheBust },
      }),
      api.get(`/contest-catalog/categories/${contestTypeId}`, {
        params: { _t: cacheBust },
      }),
      api.get('/contest-catalog/grades', {
        params: { _t: cacheBust },
      }),
    ]);

    const skills: ApiCatalogSkill[] = Array.isArray(skillsRes.data?.data) ? skillsRes.data.data : [];
    const categories: ApiCategoryRow[] = Array.isArray(catsRes.data?.data) ? catsRes.data.data : [];
    const platformGrades: string[] = visibleContestGradeCodes(
      Array.isArray(gradesRes.data?.data)
        ? gradesRes.data.data.map((g: { code: string }) => g.code)
        : [],
    );

    if (skills.length === 0) {
      return { catalog: seed, fromDb: false };
    }

    const typeLabel =
      CONTEST_TYPE_OPTIONS.find((o) => o.id === contestTypeId)?.label ?? 'Developmental & Performance Capabilities';

    const categoryGroups = buildCategoryGroupsFromDb(
      skills,
      categories,
      seed,
      contestTypeId,
      platformGrades,
    );

    return {
      catalog: {
        sport: sportName,
        contestTypes: [
          {
            id: contestTypeId,
            label: typeLabel,
            categories: categoryGroups,
          },
        ],
      },
      fromDb: true,
    };
  } catch (error) {
    console.warn('[contestCatalog] Failed to load DB catalog, using seed fallback:', error);
    return { catalog: seed, fromDb: false };
  }
}
